/**
 * Subscription context.
 *
 * Exposes the plan, quota usage and a `requirePro` helper for gating UI.
 *
 * The values here are for presentation. The API independently enforces every
 * limit, so a tampered client gains nothing — it would simply see a quota
 * error from the server instead of a paywall from the client.
 */

import { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { queryKeys } from '@chordbookpro/api-client';
import { PLAN, PLAN_LIMITS, PAYWALL_CONTEXT, FEATURE_TO_PAYWALL_CONTEXT } from '@chordbookpro/shared';
import { api } from '../services/api.js';
import { useAuth } from './AuthProvider.jsx';
import { useUiStore } from '../stores/uiStore.js';
import * as subscriptionService from '../services/subscriptions/subscriptionService.js';
import * as cache from '../services/subscriptions/subscriptionCache.js';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { isAuthenticated, firebaseUser } = useAuth();
  const queryClient = useQueryClient();
  const openPaywall = useUiStore((state) => state.openPaywall);
  const showToast = useUiStore((state) => state.showToast);

  const firebaseUid = firebaseUser?.uid ?? null;

  const statusQuery = useQuery({
    queryKey: queryKeys.billing.status(),
    queryFn: async ({ signal }) => {
      try {
        const status = await api.billing.status(signal);
        cache.saveSubscription(firebaseUid, status);
        return status;
      } catch (error) {
        // A failed network call must never downgrade a paying customer — fall
        // back to the cached snapshot rather than assuming Free.
        const cached = cache.loadSubscription(firebaseUid);
        if (cached) return { ...cached, isStale: true, entitlementVerified: false };
        throw error;
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const syncMutation = useMutation({
    mutationFn: (reason) => subscriptionService.syncWithBackend(reason),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.billing.status(), data);
      cache.saveSubscription(firebaseUid, data);
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
    },
    onError: () => {
      showToast({
        message: 'We could not verify your subscription just now. Your access is unchanged.',
        tone: 'warning',
      });
    },
  });

  const status = statusQuery.data ?? null;
  const plan = status?.plan ?? PLAN.FREE;
  const isPro = Boolean(status?.isPro);

  const limits = status?.limits ?? PLAN_LIMITS[plan] ?? PLAN_LIMITS.free;
  const usage = status?.usage ?? { playlists: 0, customSongs: 0, largestPlaylistSize: 0 };
  const remaining = status?.remaining ?? { playlists: null, customSongs: null };

  /**
   * Gate a Premium-only action.
   * Returns true when allowed; otherwise opens the right paywall and returns false.
   */
  const requirePro = useCallback(
    (feature, context) => {
      if (isPro) return true;

      const resolved =
        context ??
        FEATURE_TO_PAYWALL_CONTEXT[feature] ??
        PAYWALL_CONTEXT.GENERAL_UPGRADE;

      openPaywall(resolved, { feature });
      return false;
    },
    [isPro, openPaywall],
  );

  /**
   * Maps an API quota error onto the matching paywall.
   * Used by mutation onError handlers so a limit never surfaces as a raw error.
   */
  const handleQuotaError = useCallback(
    (error) => {
      if (!error?.isPaywall) return false;
      openPaywall(error.paywallContext ?? PAYWALL_CONTEXT.GENERAL_UPGRADE, error.details ?? null);
      return true;
    },
    [openPaywall],
  );

  const value = useMemo(
    () => ({
      status,
      plan,
      isPro,
      isFree: !isPro,
      limits,
      usage,
      remaining,
      isLoading: statusQuery.isLoading,
      isStale: status?.isStale ?? false,
      entitlementVerified: status?.entitlementVerified ?? true,

      requirePro,
      handleQuotaError,

      sync: (reason = 'manual') => syncMutation.mutate(reason),
      isSyncing: syncMutation.isPending,
      refetch: statusQuery.refetch,

      // Convenience checks for disabling controls before a request is made.
      canCreatePlaylist: limits.playlists === null || usage.playlists < limits.playlists,
      canCreateCustomSong: limits.customSongs === null || usage.customSongs < limits.customSongs,
    }),
    [
      status,
      plan,
      isPro,
      limits,
      usage,
      remaining,
      statusQuery.isLoading,
      statusQuery.refetch,
      requirePro,
      handleQuotaError,
      syncMutation,
    ],
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) throw new Error('useSubscription must be used inside a SubscriptionProvider');
  return context;
}
